/**
 * dom-selector — best-effort CSS selector path for an Element.
 *
 * Used by the bug-report flow to capture which element was focused when
 * the user clicked the "+" button. The exact selector doesn't have to
 * be precise enough to re-select the node in a test — it just has to
 * give a triager a fighting chance at figuring out what control the
 * user was interacting with.
 *
 * Strategy (in order of preference):
 *   1. If the node has an `id`, use `#id` (highest signal).
 *   2. If it has a `data-testid`, use `tag[data-testid="..."]` (next
 *      best — these are stable identifiers our codebase already uses).
 *   3. Otherwise compose `tag.class1.class2` and prepend up to 4
 *      ancestor steps (so the path is contextual but not a 50-step
 *      wall of div).
 *
 * Result is always a string. Never throws. Returns 'unknown' if we
 * can't see the node at all.
 */

const MAX_DEPTH = 5;
const MAX_CLASS_TOKENS = 3;
const MAX_SELECTOR_LEN = 300;

function tokensForElement(el: Element): string {
  const tag = el.tagName.toLowerCase();
  if (el.id) {
    return `${tag}#${el.id}`;
  }
  const testId = el.getAttribute('data-testid');
  if (testId) {
    // Quote-safe: data-testid values in this codebase are kebab-case
    // identifiers, so a CSS attribute selector is fine.
    return `${tag}[data-testid="${testId}"]`;
  }
  const className =
    typeof el.className === 'string'
      ? el.className
      : (el as unknown as { className?: { baseVal?: string } }).className
          ?.baseVal ?? '';
  if (className) {
    const tokens = className
      .split(/\s+/)
      .filter((t) => t && /^[a-zA-Z][a-zA-Z0-9_\-]*$/.test(t))
      .slice(0, MAX_CLASS_TOKENS);
    if (tokens.length > 0) {
      return `${tag}.${tokens.join('.')}`;
    }
  }
  return tag;
}

/**
 * Build a CSS selector path for `el`. The path walks up to MAX_DEPTH
 * ancestors and joins with ` > `. If the path exceeds
 * MAX_SELECTOR_LEN chars, the leftmost (root-ward) ancestors are
 * dropped to keep the output readable.
 */
export function selectorPathForElement(el: Element | null | undefined): string {
  if (!el) return 'unknown';
  const parts: string[] = [];
  let cursor: Element | null = el;
  let depth = 0;
  while (cursor && depth < MAX_DEPTH) {
    parts.unshift(tokensForElement(cursor));
    if (cursor.id) {
      // Anchor at the nearest id-bearing ancestor — the selector below
      // it is already unique enough.
      break;
    }
    cursor = cursor.parentElement;
    depth++;
  }
  let out = parts.join(' > ');
  while (out.length > MAX_SELECTOR_LEN && parts.length > 1) {
    parts.shift();
    out = parts.join(' > ');
  }
  return out;
}

/**
 * Convenience: read `document.activeElement` and produce its selector
 * path. Returns null when there's no meaningful focused element (body
 * or unknown).
 */
export function activeElementSelector(): string | null {
  if (typeof document === 'undefined') return null;
  const el = document.activeElement;
  if (!el || el === document.body) return null;
  return selectorPathForElement(el);
}
